import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'wouter';
import { ArrowRight } from 'lucide-react';

interface CaseStudy {
  title: string;
  description: string;
  image: string;
  learnMoreLink?: string;
}

const caseStudies: CaseStudy[] = [
  {
    title: 'caseStudies.fire.title',
    description: 'caseStudies.fire.description',
    image: '/case-studies/fire-detection.png',
    learnMoreLink: '/case-studies/fire-detection'
  },
  {
    title: 'caseStudies.fall.title',
    description: 'caseStudies.fall.description',
    image: '/case-studies/fall-detection.png',
    learnMoreLink: '/case-studies/fall-detection'
  },
  {
    title: 'caseStudies.violence.title',
    description: 'caseStudies.violence.description',
    image: '/case-studies/Violance-detection.png',
    learnMoreLink: '/case-studies/violence-detection'
  },
  {
    title: 'caseStudies.shoplift.title',
    description: 'caseStudies.shoplift.description',
    image: '/case-studies/Shoplifting-detection.png',
    learnMoreLink: '/case-studies/shoplifting-detection'
  }
];

const CaseStudies: React.FC = () => {
  const { t } = useTranslation();
  const [isPaused, setIsPaused] = React.useState(false);
  const [position, setPosition] = React.useState(0);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const animationRef = React.useRef<number>();
  const lastTimeRef = React.useRef<number>(0);
  const speed = 60; // pixels per second (increased from 30)
  
  // Animation effect for infinite loop
  React.useEffect(() => {
    const animate = (timestamp: number) => {
      if (!lastTimeRef.current) lastTimeRef.current = timestamp;
      const delta = timestamp - lastTimeRef.current;
      lastTimeRef.current = timestamp;

      if (!isPaused) {
        setPosition(prev => {

          const itemWidth = 380 + 32; // Width of item + gap (32px = 2rem)
          const totalItems = caseStudies.length;
          const totalWidth = itemWidth * totalItems;
          
          // Calculate new position
          let newPosition = prev - (speed * delta) / 1000;
          
          // Reset position when reaching the end of the duplicated items
          if (Math.abs(newPosition) >= totalWidth) {
            newPosition = 0;
          }
          
          return newPosition;
        });
      }
      
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isPaused]);

  return (
    <section id="case-studies" className="py-24 dark:bg-slate-900">
      <div className="w-full">
        <div className="text-center mb-12 px-8">
          <h2 className="text-4xl font-bold mb-6 text-gray-900 dark:text-slate-100">{t('caseStudies.title')}</h2>
          <p className="text-lg text-gray-600 dark:text-slate-400 max-w-3xl mx-auto">{t('caseStudies.description')}</p>
        </div>
        
        <div 
          ref={containerRef}
          className="relative w-full overflow-hidden"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => {
            setIsPaused(false);
            lastTimeRef.current = performance.now();
          }}
        >
          <div
            className="flex items-center py-8"
            style={{
              transform: `translateX(${position}px)`,
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'stretch',
              gap: '2rem',
              width: 'max-content',
              padding: '0 2rem',
              willChange: 'transform',
            }}
          >
            {[...caseStudies, ...caseStudies, ...caseStudies].map((study, index) => (
              <div 
                key={index}
                className="group relative bg-white dark:bg-slate-800/50 rounded-3xl border border-gray-200 dark:border-slate-700/50 p-6 transition-all duration-300 hover:border-primary/10 dark:hover:border-primary-5 flex-shrink-0 flex flex-col"
                style={{
                  width: '380px',
                  minHeight: '580px',
                  flex: '0 0 auto'
                }}
              >
                <div className="relative w-full h-[280px] mb-6 rounded-2xl overflow-hidden flex-shrink-0">
                  <img 
                    src={study.image} 
                    alt={t(study.title)} 
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" 
                    loading="lazy"
                  />
                </div>
                <div className="flex flex-col flex-grow justify-between">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-slate-100 mb-4 line-clamp-2">
                      {t(study.title)}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-slate-300/90 leading-relaxed line-clamp-5 mb-4">
                      {t(study.description)}
                    </p>
                  </div>
                  {study.learnMoreLink && (
                    <Link 
                      href={study.learnMoreLink}
                      className="mt-4 inline-flex items-center text-primary-700 dark:text-primary-300 font-medium hover:underline hover:text-primary-900 dark:hover:text-primary-100 transition-colors duration-200 group"
                    >
                      {t('common.learnMore')}
                      <ArrowRight className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform duration-200" />
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
          

        </div>
      </div>
    </section>
  );
};

export default CaseStudies;
